/**
 * AI price intelligence feed — chat-style analyst stream above the historical chart.
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Bot, RefreshCw, Radio } from 'lucide-react';
import { localTodayIsoDate } from '@/utils/inclusiveCalendarDateRange';
import { LivePriceProvider, useLivePrice } from '@/contexts/LivePriceContext';
import {
  stockService,
  type PriceIntelligenceFeedPayload,
  type PriceIntelligenceFeedItem,
  type NextDayPricePredictionPayload,
} from '@/services/api';
import {
  MarketViewPredictionEntry,
  type MarketViewData,
} from '@/components/feed/MarketViewPredictionEntry';
import type { YearlyPriceVolumePayload } from '@/utils/volumeByPriceLayer';
import { resolveYearlyVolumeByPriceBucketCents } from '@/utils/volumeByPriceLayer';
import {
  buildVolumeProfileFeedBlurb,
  yearlyVolumeRangeForFeed,
} from '@/utils/volumeProfileFeedCopy';
import { PRICE_INTELLIGENCE_FEED_ENABLED } from '@/config/features';

const LIVE_PRICE_PREF_KEY = 'mf_live_price_ws_enabled';

function readLivePricePref(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(LIVE_PRICE_PREF_KEY) !== 'false';
}

function persistLivePricePref(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LIVE_PRICE_PREF_KEY, enabled ? 'true' : 'false');
}

export type PriceIntelligenceChartBar = {
  date: string;
  open?: number;
  high?: number;
  low?: number;
  close: number;
  volume?: number;
  pct_change?: number;
};

type Props = {
  ticker: string;
  date: string | null;
  ohlc: PriceIntelligenceChartBar | null;
  recentBars: PriceIntelligenceChartBar[];
  /** When the chart already loaded yearly volume-at-price, pass through to avoid a second fetch. */
  yearlyPriceVolume?: YearlyPriceVolumePayload | null;
  className?: string;
};

type FeedMessage = {
  id: string;
  timestamp: string;
  content: string;
};

function fmtClock(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtUsd(price: number): string {
  return price.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Spread timestamps across regular hours (9:30 AM → ~4:00 PM ET). */
function buildMessageTimestamps(date: string, count: number): string[] {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const sessionOpen = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 9, 30, 0)
    : new Date();
  const sessionMs = 6.5 * 60 * 60 * 1000;
  const step = count > 1 ? sessionMs / (count - 1) : 0;
  return Array.from({ length: count }, (_, i) =>
    fmtClock(new Date(sessionOpen.getTime() + i * step)),
  );
}

function itemsToMessages(
  headline: string | undefined,
  items: PriceIntelligenceFeedItem[],
  date: string,
): FeedMessage[] {
  const bodies: string[] = [];
  if (headline?.trim()) {
    bodies.push(headline.trim());
  }
  for (const item of items) {
    const text = item.body?.trim();
    if (!text) continue;
    if (bodies.length > 0 && bodies[bodies.length - 1] === text) continue;
    bodies.push(text);
  }
  const stamps = buildMessageTimestamps(date, bodies.length);
  return bodies.map((content, i) => ({
    id: `msg-${i}`,
    timestamp: stamps[i] ?? fmtClock(new Date()),
    content,
  }));
}

function predictionTimestamp(iso?: string): string {
  if (!iso) return '12:00 PM';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '12:00 PM';
  return fmtClock(d);
}

function isMarketViewComplete(view: NextDayPricePredictionPayload['market_view']): view is MarketViewData {
  if (!view) return false;
  return Boolean(
    view.market_state?.trim() &&
      view.current_thesis?.trim() &&
      view.invalidation?.trim() &&
      view.next_likely_move?.trim(),
  );
}

function footerLabel(sources?: string[]): string {
  if (!sources?.length) {
    return 'Powered by AI • Liquidity + Volume + Order Flow';
  }
  const labels = sources
    .map((s) => {
      if (s === 'trade_session_stats') return 'Order Flow';
      if (s === 'day_narrative') return 'Tape Narrative';
      if (s === 'stock_move_explanation') return 'Catalysts';
      if (s === 'chart_recent_bars') return 'Price History';
      if (s === 'chart_ohlc') return 'OHLC';
      if (s === 'yearly_price_volume') return 'Volume Profile';
      if (s === 'next_day_prediction') return 'Next-Day Forecast';
      return null;
    })
    .filter(Boolean);
  const unique = [...new Set(labels)];
  if (unique.length === 0) {
    return 'Powered by AI • Liquidity + Volume + Order Flow';
  }
  return `Powered by AI • ${unique.join(' + ')}`;
}

function FeedEntry({ timestamp, content }: { timestamp: string; content: string }) {
  const lines = content.split('\n').map((l) => l.trim()).filter(Boolean);

  return (
    <article className="space-y-3">
      <time className="block text-sm font-bold text-gray-900">{timestamp}</time>
      <div className="border-l-2 border-gray-200 pl-4">
        {lines.length > 1 ? (
          <div className="space-y-1 text-[13px] leading-relaxed text-gray-900">
            {lines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        ) : (
          <p className="text-[13px] leading-relaxed text-gray-900">{content}</p>
        )}
      </div>
    </article>
  );
}

function TypingIndicator() {
  return (
    <motion.article
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-3"
    >
      <p className="text-sm font-bold text-gray-400">—</p>
      <div className="border-l-2 border-gray-200 pl-4">
        <div className="flex items-center gap-1.5">
          <motion.div
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.4, repeat: Infinity }}
            className="h-1 w-1 rounded-full bg-gray-400"
          />
          <motion.div
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.4, repeat: Infinity, delay: 0.2 }}
            className="h-1 w-1 rounded-full bg-gray-400"
          />
          <motion.div
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.4, repeat: Infinity, delay: 0.4 }}
            className="h-1 w-1 rounded-full bg-gray-400"
          />
          <span className="ml-1 text-[13px] text-gray-500">Analyzing…</span>
        </div>
      </div>
    </motion.article>
  );
}

export function PriceIntelligenceFeed(props: Props) {
  if (!PRICE_INTELLIGENCE_FEED_ENABLED) {
    return null;
  }

  const isSessionToday = props.date === localTodayIsoDate();
  const [showLivePrice, setShowLivePrice] = useState(readLivePricePref);

  const toggleLivePrice = useCallback(() => {
    setShowLivePrice((prev) => {
      const next = !prev;
      persistLivePricePref(next);
      return next;
    });
  }, []);

  return (
    <LivePriceProvider
      symbol={props.ticker}
      enabled={isSessionToday && showLivePrice}
    >
      <PriceIntelligenceFeedBody
        {...props}
        isSessionToday={isSessionToday}
        showLivePrice={showLivePrice}
        onToggleLivePrice={toggleLivePrice}
      />
    </LivePriceProvider>
  );
}

type BodyProps = Props & {
  isSessionToday: boolean;
  showLivePrice: boolean;
  onToggleLivePrice: () => void;
};

function PriceIntelligenceFeedBody({
  ticker,
  date,
  ohlc,
  recentBars,
  yearlyPriceVolume: yearlyPriceVolumeProp,
  className = '',
  isSessionToday,
  showLivePrice,
  onToggleLivePrice,
}: BodyProps) {
  const [feed, setFeed] = useState<PriceIntelligenceFeedPayload | null>(null);
  const [nextDayPrediction, setNextDayPrediction] =
    useState<NextDayPricePredictionPayload | null>(null);
  const [predictionRefreshing, setPredictionRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [yearlyPriceVolumeFetched, setYearlyPriceVolumeFetched] =
    useState<YearlyPriceVolumePayload | null>(null);
  const [currentTime, setCurrentTime] = useState(() => new Date());

  const {
    livePrice,
    connected: livePriceConnected,
    streaming: livePriceStreaming,
    error: livePriceError,
    notice: livePriceNotice,
    feedMode: liveFeedMode,
  } = useLivePrice();

  const displayPrice = showLivePrice ? (livePrice ?? ohlc?.close ?? null) : null;

  const requestKey = useMemo(
    () => (date ? `${ticker}:${date}:${ohlc?.close ?? ''}:${recentBars.length}` : null),
    [ticker, date, ohlc?.close, recentBars.length],
  );

  const loadFeed = useCallback(
    (forceRefresh = false) => {
      if (!date || !ohlc) return;
      setLoading(true);
      setError(null);
      stockService
        .getPriceIntelligenceFeed(ticker, {
          date,
          ohlc: {
            open: ohlc.open,
            high: ohlc.high,
            low: ohlc.low,
            close: ohlc.close,
            volume: ohlc.volume,
            pct_change: ohlc.pct_change,
          },
          recent_bars: recentBars.slice(-20).map((b) => ({
            date: b.date,
            close: b.close,
            pct_change: b.pct_change,
          })),
          force_refresh: forceRefresh,
        })
        .then((result) => {
          if (result?.success === false && result?.error) {
            setError(result.error);
            setFeed(null);
          } else {
            setFeed(result);
            setError(null);
          }
        })
        .catch((err: Error) => {
          setError(err.message);
          setFeed(null);
        })
        .finally(() => setLoading(false));
    },
    [ticker, date, ohlc, recentBars],
  );

  useEffect(() => {
    if (!requestKey) {
      setFeed(null);
      return;
    }
    setFeed(null);
    loadFeed(false);
  }, [requestKey, loadFeed]);

  const loadNextDayPrediction = useCallback(() => {
    if (!date) {
      setNextDayPrediction(null);
      return Promise.resolve();
    }
    return stockService.getNextDayPricePrediction(ticker, date).then((result) => {
      setNextDayPrediction(result);
    });
  }, [ticker, date]);

  useEffect(() => {
    let cancelled = false;
    loadNextDayPrediction().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [loadNextDayPrediction]);

  const refreshPredictionWithAfterHours = useCallback(() => {
    if (!date) return;
    setPredictionRefreshing(true);
    const ahClose =
      showLivePrice && livePrice != null && Number.isFinite(livePrice) ? livePrice : undefined;
    stockService
      .refreshNextDayPricePrediction(ticker, {
        as_of: date,
        after_hours_close: ahClose,
        include_after_hours: true,
        refresh_after_hours_cache: true,
      })
      .then((result) => {
        if (result) setNextDayPrediction(result);
      })
      .finally(() => setPredictionRefreshing(false));
  }, [ticker, date, showLivePrice, livePrice]);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const yearlyVolumeKey = date ? `${ticker}:${date}` : null;

  useEffect(() => {
    if (!yearlyVolumeKey || !date) {
      setYearlyPriceVolumeFetched(null);
      return;
    }
    if (yearlyPriceVolumeProp?.price_levels?.length) {
      setYearlyPriceVolumeFetched(null);
      return;
    }
    const { start, end } = yearlyVolumeRangeForFeed(date);
    const bucketCents = resolveYearlyVolumeByPriceBucketCents(ohlc?.close ?? null);
    let cancelled = false;
    stockService
      .getYearlyPriceVolume(ticker, start, end, 'extended', bucketCents)
      .then((doc) => {
        if (!cancelled) setYearlyPriceVolumeFetched(doc);
      })
      .catch(() => {
        if (!cancelled) setYearlyPriceVolumeFetched(null);
      });
    return () => {
      cancelled = true;
    };
  }, [yearlyVolumeKey, ticker, date, ohlc?.close, yearlyPriceVolumeProp]);

  const yearlyPriceVolume =
    yearlyPriceVolumeProp?.price_levels?.length ? yearlyPriceVolumeProp : yearlyPriceVolumeFetched;

  const volumeProfileBlurb = useMemo(() => {
    if (!date || !ohlc) return null;
    return buildVolumeProfileFeedBlurb(yearlyPriceVolume, date, {
      high: ohlc.high,
      low: ohlc.low,
      close: ohlc.close,
    });
  }, [yearlyPriceVolume, date, ohlc]);

  const { narrativeMessages, volumeProfileMessage } = useMemo(() => {
    if (!date || !feed) {
      return { narrativeMessages: [] as FeedMessage[], volumeProfileMessage: null as FeedMessage | null };
    }
    const base = itemsToMessages(feed.headline, feed.items ?? [], date);
    if (!volumeProfileBlurb) {
      return { narrativeMessages: base, volumeProfileMessage: null };
    }
    return {
      narrativeMessages: base,
      volumeProfileMessage: {
        id: 'volume-profile',
        timestamp: '4:00 PM',
        content: volumeProfileBlurb,
      },
    };
  }, [feed, date, volumeProfileBlurb]);

  const marketViewPrediction = useMemo(() => {
    if (nextDayPrediction?.success !== true) return null;
    const view = nextDayPrediction.market_view;
    if (!isMarketViewComplete(view)) return null;
    return {
      timestamp: predictionTimestamp(nextDayPrediction.generated_at),
      marketView: view,
    };
  }, [nextDayPrediction]);

  const sourcesUsed = useMemo(() => {
    const src = [...(feed?.sources_used ?? [])];
    if (volumeProfileBlurb && !src.includes('yearly_price_volume')) {
      src.push('yearly_price_volume');
    }
    if (marketViewPrediction && !src.includes('next_day_prediction')) {
      src.push('next_day_prediction');
    }
    return src;
  }, [feed?.sources_used, volumeProfileBlurb, marketViewPrediction]);

  if (!date) return null;

  const statusLabel = isSessionToday ? (showLivePrice ? 'Live' : 'Paused') : 'Session';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`mb-8 ${className}`}
    >
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-gray-900 to-gray-700">
              <Bot className="h-3.5 w-3.5 text-white" />
            </div>
            <div>
              <div className="text-sm text-gray-900">AI Analyst</div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <motion.div
                  animate={{ opacity: loading ? [1, 0.4, 1] : 1 }}
                  transition={{ duration: 2, repeat: loading ? Infinity : 0, ease: 'easeInOut' }}
                  className={`h-1 w-1 rounded-full ${
                    loading
                      ? 'bg-amber-500'
                      : isSessionToday && showLivePrice
                        ? 'bg-emerald-500'
                        : 'bg-gray-400'
                  }`}
                />
                <span className="text-[10px] text-gray-500">
                  {loading ? 'Updating' : statusLabel} · {ticker}
                  {isSessionToday && showLivePrice && displayPrice != null ? (
                    <span className="ml-1 inline-flex items-center gap-1 font-mono text-gray-700">
                      <span>·</span>
                      <motion.span
                        key={displayPrice}
                        initial={{ color: '#059669', scale: 1.04 }}
                        animate={{ color: '#374151', scale: 1 }}
                        transition={{ duration: 0.45 }}
                      >
                        {fmtUsd(displayPrice)}
                      </motion.span>
                      {livePrice != null && livePriceConnected && showLivePrice ? (
                        <span
                          className={
                            liveFeedMode === 'realtime' && livePriceStreaming
                              ? 'text-emerald-600'
                              : 'text-gray-500'
                          }
                        >
                          {liveFeedMode === 'realtime' && livePriceStreaming
                            ? ' live'
                            : liveFeedMode === 'delayed'
                              ? ' delayed'
                              : ' updating'}
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSessionToday ? (
              <button
                type="button"
                role="switch"
                aria-checked={showLivePrice}
                title={showLivePrice ? 'Turn off live price feed' : 'Turn on live price feed'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleLivePrice();
                }}
                className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[10px] font-semibold transition-all ${
                  showLivePrice
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                    : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Radio className={`h-3 w-3 ${showLivePrice ? 'text-emerald-600' : 'text-gray-500'}`} />
                {showLivePrice ? 'Live price on' : 'Live price off'}
              </button>
            ) : null}
            <button
              type="button"
              title="Refresh feed and re-run next-day forecast with after-hours context"
              disabled={loading || predictionRefreshing}
              onClick={() => {
                loadFeed(true);
                refreshPredictionWithAfterHours();
              }}
              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-700 disabled:opacity-40"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${loading || predictionRefreshing ? 'animate-spin' : ''}`}
              />
            </button>
            <div className="font-mono text-[10px] text-gray-400">
              {isSessionToday
                ? currentTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                : date.replace(/-/g, '.')}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="max-h-[420px] space-y-8 overflow-y-auto px-6 py-6">
          <AnimatePresence mode="popLayout">
            {error && !loading ? (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="border-l-2 border-amber-200 pl-4"
              >
                <p className="text-[13px] leading-relaxed text-amber-800">{error}</p>
              </motion.div>
            ) : null}

            {narrativeMessages.map((message, index) => (
              <motion.div
                key={`${date}-${message.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <FeedEntry timestamp={message.timestamp} content={message.content} />
              </motion.div>
            ))}

            {marketViewPrediction ? (
              <motion.div
                key={`${date}-market-view-prediction`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: narrativeMessages.length * 0.08 }}
              >
                <MarketViewPredictionEntry
                  timestamp={marketViewPrediction.timestamp}
                  marketView={marketViewPrediction.marketView}
                />
              </motion.div>
            ) : null}

            {volumeProfileMessage ? (
              <motion.div
                key={`${date}-${volumeProfileMessage.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  delay: (narrativeMessages.length + (marketViewPrediction ? 1 : 0)) * 0.08,
                }}
              >
                <FeedEntry
                  timestamp={volumeProfileMessage.timestamp}
                  content={volumeProfileMessage.content}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>

          <AnimatePresence>
            {loading ? <TypingIndicator key="typing" /> : null}
          </AnimatePresence>

          {!loading && !error && narrativeMessages.length === 0 && !marketViewPrediction ? (
            <p className="text-[13px] text-gray-500">Waiting for analysis…</p>
          ) : null}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-3">
          <p className="text-[10px] text-gray-400">
            {footerLabel(sourcesUsed)}
            {feed?.from_cache ? ' · cached' : null}
            {nextDayPrediction?.from_cache === false ? ' · forecast refreshed' : null}
            {predictionRefreshing ? ' · updating forecast…' : null}
            {isSessionToday && showLivePrice && liveFeedMode === 'realtime'
              ? ' · Massive real-time'
              : null}
            {isSessionToday && showLivePrice && liveFeedMode === 'delayed'
              ? ' · Massive delayed (~15m)'
              : null}
            {isSessionToday && showLivePrice && !liveFeedMode ? ' · price via Massive' : null}
            {showLivePrice && livePriceNotice ? ` · ${livePriceNotice}` : null}
            {showLivePrice && livePriceError ? ` · ${livePriceError}` : null}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
